/* eslint-disable react/no-unescaped-entities */
import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
    Sparkles, RefreshCw, FileDown, History, TrendingUp, TrendingDown,
    AlertTriangle, Flag, Target, ListChecks, Users, Loader2, ChevronRight,
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import api from '../../lib/api';

// Map de styles par type de section (titre **XXX**)
const SECTION_META = [
    { keys: ['SYNTHESE', 'SYNTHÈSE', 'RESUME', 'RÉSUMÉ'], icon: Sparkles, color: '#A855F7' },
    { keys: ['FORCE'], icon: TrendingUp, color: '#10B981' },
    { keys: ['FAIBLESSE', 'AXES'], icon: AlertTriangle, color: '#F59E0B' },
    { keys: ['NATIONAL', 'POSITIONNEMENT'], icon: Flag, color: '#3B82F6' },
    { keys: ['SOURCE', 'ACQUISITION'], icon: Target, color: '#FF914D' },
    { keys: ['PLAN', 'ACTION', 'RECOMMAND'], icon: ListChecks, color: '#681793' },
    { keys: ['FOCUS', 'AGENT'], icon: Users, color: '#6366F1' },
];

function metaFor(title) {
    const up = (title || '').toUpperCase();
    for (const m of SECTION_META) {
        if (m.keys.some((k) => up.includes(k))) return m;
    }
    return { icon: Sparkles, color: '#681793' };
}

function parseSections(text) {
    const lines = (text || '').split('\n');
    const sections = [];
    let cur = null;
    const headerRe = /^\s*\*\*(.+?)\*\*\s*:?\s*$/;
    for (const raw of lines) {
        const m = raw.match(headerRe);
        if (m) {
            cur = { title: m[1].trim(), items: [] };
            sections.push(cur);
        } else if (raw.trim()) {
            if (!cur) { cur = { title: '', items: [] }; sections.push(cur); }
            cur.items.push(raw.trim());
        }
    }
    return sections.length ? sections : [{ title: '', items: [text] }];
}

function renderInline(text) {
    const parts = String(text).split(/\*\*(.+?)\*\*/g);
    return parts.map((p, i) =>
        i % 2 === 1
            ? <strong key={i} className="text-foreground font-semibold">{p}</strong>
            : <span key={i}>{p}</span>
    );
}

function SectionCard({ title, items }) {
    const meta = metaFor(title);
    const Icon = meta.icon;
    return (
        <div className="rounded-xl border border-border bg-card/60 p-4" data-testid="agency-ai-section">
            {title && (
                <div className="flex items-center gap-2 mb-2.5">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${meta.color}1f` }}>
                        <Icon className="w-4 h-4" style={{ color: meta.color }} />
                    </div>
                    <h4 className="text-[13px] font-bold text-foreground uppercase tracking-wide">{title}</h4>
                </div>
            )}
            <div className="space-y-1.5">
                {items.map((it, i) => {
                    const bullet = /^[-*•]/.test(it);
                    const clean = it.replace(/^[-*•]\s*/, '');
                    return (
                        <div key={i} className={`text-[13px] leading-relaxed text-foreground/80 ${bullet ? 'flex gap-2' : ''}`}>
                            {bullet && <span className="mt-[7px] w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: meta.color }} />}
                            <span>{renderInline(clean)}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default function AgencyAIAnalysisCard() {
    const [summary, setSummary] = useState(null);
    const [generating, setGenerating] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [history, setHistory] = useState(null);
    const [showHistory, setShowHistory] = useState(false);
    const [viewingDate, setViewingDate] = useState(null);

    const loadHistory = async () => {
        try {
            const res = await api.get('/admin/agency/ai-summary/history');
            setHistory(res.data.history || []);
        } catch { setHistory([]); }
    };

    const generate = async () => {
        setGenerating(true);
        try {
            const res = await api.get('/admin/agency/ai-summary');
            setSummary(res.data.summary);
            setViewingDate(null);
            if (showHistory) loadHistory();
            toast.success('Analyse générée');
        } catch {
            toast.error("Impossible de générer l'analyse");
        } finally { setGenerating(false); }
    };

    const toggleHistory = () => {
        const next = !showHistory;
        setShowHistory(next);
        if (next && history === null) loadHistory();
    };

    const exportPdf = async (date) => {
        setExporting(true);
        try {
            const url = '/admin/agency/ai-summary/pdf' + (date ? `?date=${date}` : '');
            const res = await api.get(url, { responseType: 'blob' });
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = `analyse-agence-${date || 'recente'}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            setTimeout(() => window.URL.revokeObjectURL(link.href), 1000);
            toast.success('PDF téléchargé');
        } catch {
            toast.error("Aucune analyse à exporter — générez d'abord une analyse");
        } finally { setExporting(false); }
    };

    const viewSnapshot = (snap) => {
        setSummary(snap.summary);
        setViewingDate(snap.date);
    };

    const sections = summary ? parseSections(summary) : [];

    return (
        <div
            className="bg-gradient-to-br from-[#681793]/10 to-[#FF914D]/5 rounded-2xl border border-[#681793]/20 p-5"
            data-testid="agency-ai-analysis"
        >
            {/* En-tête + actions */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div>
                    <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-[#FF914D]" />
                        Analyse IA de l'agence
                    </h3>
                    {viewingDate && (
                        <p className="text-[11px] text-muted-foreground mt-0.5">Analyse archivée du {viewingDate}</p>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {summary && (
                        <Button
                            size="sm" variant="outline"
                            onClick={() => exportPdf(viewingDate)}
                            disabled={exporting}
                            className="border-border text-foreground/80 text-xs h-8"
                            data-testid="agency-ai-pdf"
                        >
                            {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <FileDown className="w-3.5 h-3.5 mr-1" />}
                            PDF
                        </Button>
                    )}
                    <Button
                        size="sm" variant="outline"
                        onClick={toggleHistory}
                        className={`text-xs h-8 ${showHistory ? 'border-[#681793]/40 text-[#A855F7]' : 'border-border text-foreground/80'}`}
                        data-testid="agency-ai-history-toggle"
                    >
                        <History className="w-3.5 h-3.5 mr-1" />
                        Historique{history?.length ? ` (${history.length})` : ''}
                    </Button>
                    <Button
                        size="sm"
                        onClick={generate}
                        disabled={generating}
                        className="bg-gradient-to-r from-[#681793] to-[#FF914D] hover:opacity-90 text-white text-xs h-8"
                        data-testid="agency-ai-generate"
                    >
                        {generating ? <RefreshCw className="w-3.5 h-3.5 animate-spin mr-1" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                        {generating ? 'Analyse…' : summary ? 'Actualiser' : "Générer l'analyse"}
                    </Button>
                </div>
            </div>

            {/* Historique / trajectoire */}
            <AnimatePresence>
                {showHistory && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden mb-4"
                    >
                        <div className="rounded-xl border border-border bg-card/50 p-3" data-testid="agency-ai-history">
                            <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5">
                                <History className="w-3.5 h-3.5" /> Trajectoire de l'agence
                            </p>
                            {history && history.length > 0 ? (
                                <div className="space-y-1">
                                    {history.map((h, i) => {
                                        const prev = history[i + 1];
                                        const conv = h.metrics?.conversion_rate || 0;
                                        const delta = prev ? conv - (prev.metrics?.conversion_rate || 0) : 0;
                                        const active = (viewingDate || history[0]?.date) === h.date;
                                        return (
                                            <div
                                                key={h.date}
                                                className={`flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg text-xs transition-colors ${active ? 'bg-[#681793]/15' : 'hover:bg-white/5'}`}
                                            >
                                                <button
                                                    onClick={() => viewSnapshot(h)}
                                                    className="flex items-center gap-2 text-left flex-1 min-w-0"
                                                    data-testid={`agency-ai-history-item-${h.date}`}
                                                >
                                                    <ChevronRight className="w-3 h-3 text-muted-foreground shrink-0" />
                                                    <span className="font-mono text-foreground">{h.date}</span>
                                                    <span className="text-muted-foreground truncate">
                                                        · {h.metrics?.sold || 0} ventes · {conv}% conv. · {h.metrics?.exclusivity_rate || 0}% excl.
                                                    </span>
                                                    {delta !== 0 && (
                                                        <span className={`flex items-center gap-0.5 ${delta > 0 ? 'text-green-400' : 'text-red-400'}`}>
                                                            {delta > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                                                            {Math.abs(delta).toFixed(1)}pt
                                                        </span>
                                                    )}
                                                </button>
                                                <button
                                                    onClick={() => exportPdf(h.date)}
                                                    className="text-[#FF914D] hover:underline shrink-0"
                                                >
                                                    PDF
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <p className="text-xs text-muted-foreground">
                                    Aucun historique pour l'instant. Chaque analyse générée est sauvegardée ici (1 par jour) pour suivre l'évolution de votre agence.
                                </p>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Contenu structuré */}
            {summary ? (
                <div className="grid sm:grid-cols-2 gap-3" data-testid="agency-ai-content">
                    {sections.map((s, i) => (
                        <div key={i} className={s.title && /SYNTHESE|SYNTHÈSE|PLAN|ACTION|FOCUS/i.test(s.title) ? 'sm:col-span-2' : ''}>
                            <SectionCard title={s.title} items={s.items} />
                        </div>
                    ))}
                </div>
            ) : (
                <p className="text-xs text-muted-foreground">
                    Cliquez sur "Générer l'analyse" pour obtenir un diagnostic IA structuré des performances de votre agence — exportable en PDF et archivé pour suivre votre trajectoire.
                </p>
            )}
        </div>
    );
}
